<script lang="ts" setup>
import type { FdvAtTgePreferenceEnumType } from '@forgd/shared/enums'
import { useMedianLaunchFdv } from '#public-research/composables/useMedianLaunchFdv'
import { median } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { Table, TableBody, TableCell, TableRow } from '@forgd/ui-kit/table'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

const fdvQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/fdv-by-initial-circ-supply', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const priceChangeQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/price-change-vs-initial-circ-supply', {
    params: () => ({ query: { fdvBucket: 'no_preference' as FdvAtTgePreferenceEnumType } }),
  }),
  refetchOnWindowFocus: false,
})

const { medianFdv, isLoading: isMedianLoading } = useMedianLaunchFdv()

const isLoading = computed(() =>
  fdvQuery.isLoading.value || priceChangeQuery.isLoading.value || isMedianLoading.value,
)

const medianLaunchFdv = computed(() =>
  medianFdv.value === null ? null : formatValue(medianFdv.value, 'currency:short,2'),
)

const projectsTracked = computed(() => {
  const qty = fdvQuery.data.value?.tokensQty
  return typeof qty === 'number' ? formatValue(qty, 'number:0') : null
})

const medianDay1Float = computed(() => {
  const tokens = priceChangeQuery.data.value?.timeframes?.latest?.tokens ?? []
  const value = median(tokens.map(t => t.initialCircSupplyPercentage))
  return value === null ? null : formatValue(value, 'percent:1')
})

const median30DayReturn = computed(() => {
  const tokens = priceChangeQuery.data.value?.timeframes?.['30d']?.tokens ?? []
  const value = median(tokens.map(t => t.priceChangePercentage))
  return value === null ? null : formatValue(value, 'percent:1')
})

const rows = computed(() => [
  { label: 'Projects Tracked', value: projectsTracked.value },
  { label: 'Median Day-1 Float', value: medianDay1Float.value },
  { label: 'Median 30-Day Return', value: median30DayReturn.value },
])
</script>

<template>
  <Card class="flex h-full flex-col">
    <CardHeader class="flex flex-row items-center justify-between border-b border-default p-3 sm:pl-5">
      <CardTitle>
        Tokenomics Research
      </CardTitle>
      <Button
        as-child
        variant="outline"
        size="sm"
        icon-only
      >
        <NuxtLink to="/research/tokenomics" aria-label="Open Tokenomics Research">
          <ArrowRightIcon />
        </NuxtLink>
      </Button>
    </CardHeader>

    <div class="flex flex-1 flex-col gap-4 p-3 sm:p-4">
      <div class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <span class="text-2xs text-secondary">Median Launch FDV</span>
          <Skeleton v-if="isLoading" class="h-5 w-20" />
          <span v-else class="text-base font-semibold text-primal">{{ medianLaunchFdv }}</span>
        </div>
        <svg
          viewBox="0 0 160 40"
          preserveAspectRatio="none"
          class="h-10 w-40 shrink-0"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="pr-tokenomics-spark" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stop-color="#16a34a" stop-opacity="0.25" />
              <stop offset="100%" stop-color="#16a34a" stop-opacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,32 L12,28 L24,30 L36,24 L48,26 L60,18 L72,21 L84,14 L96,17 L108,11 L120,15 L132,7 L144,10 L160,4 L160,40 L0,40 Z"
            fill="url(#pr-tokenomics-spark)"
          />
          <polyline
            points="0,32 12,28 24,30 36,24 48,26 60,18 72,21 84,14 96,17 108,11 120,15 132,7 144,10 160,4"
            fill="none"
            stroke="#16a34a"
            stroke-width="1.5"
          />
        </svg>
      </div>

      <div class="mt-auto overflow-hidden rounded-md border border-default text-xs">
        <Table>
          <TableBody class="divide-y divide-default">
            <TableRow v-for="row in rows" :key="row.label" class="h-8">
              <TableCell class="px-2 py-1 text-secondary">
                {{ row.label }}
              </TableCell>
              <TableCell class="px-2 py-1 text-right">
                <Skeleton v-if="isLoading" class="ml-auto h-4 w-12" />
                <span v-else class="font-mono text-xs font-medium text-primal">{{ row.value }}</span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  </Card>
</template>
